import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { FaArrowLeft, FaUserPlus } from "react-icons/fa";
import { colors } from '../../../assets/styles/GlobalStyles';
import { Button } from '../../../components/ui/Button/Button';
import { Alert } from '../../../components/ui/Alert/Alert';
import { PersonalInfoSection } from './PersonalInfoSection';
import { AddressSection } from '../../../modules/address/components/AddressSection';
import { PasswordSection } from './PasswordSection';
import { useForm } from '../../../hooks/useForm';
import { useCEP, mapearEnderecoCadastro } from '../../../hooks/useCEP';
import { INITIAL_REGISTER_FORM, VALIDATION_MESSAGES } from '../../../utils/constants';
import { addressService, cadastroService } from '../../../services';
import { validateCPF, validateEmail, validateCEP, validatePhone } from '../../../utils/validators';
import { unformat } from '../../../utils/formatters';

const FormContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  max-width: 800px;
  margin: 0 auto;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${colors.darkBlue};
  cursor: pointer;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  align-self: flex-start;
  
  &:hover { background: rgba(13, 35, 87, 0.05); }
`;

const FormTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${colors.darkBlue};
  margin-bottom: 0.5rem;
  text-align: center;
`;

const FormSubtitle = styled.p`
  color: #666;
  text-align: center;
  margin-bottom: 2rem;
  font-size: 0.9rem;
`;

// Mensagem exibida quando o CEP não é encontrado na base de endereços — o
// cadastro não avança nesse caso (ver handleCEPBlur/validationRules abaixo),
// a pessoa precisa confirmar o CEP com um atendente da OAB/MS.
const CEP_NAO_ENCONTRADO_MSG = 'CEP não encontrado em nossa base. Entre em contato com um atendente da OAB/MS pelos telefones (67) 3318-4782 ou (67) 3318-4778 para verificar esse CEP.';
const CEP_SELECIONE_ENDERECO_MSG = 'Este CEP tem mais de um endereço. Selecione o seu na lista abaixo do CEP.';

const validationRules = {
  // Informações Pessoais
  nome: [{ required: true, message: VALIDATION_MESSAGES.REQUIRED }],
  cpfCnpj: [
    { required: true, message: VALIDATION_MESSAGES.REQUIRED },
    { validator: (value) => validateCPF(value) ? null : VALIDATION_MESSAGES.INVALID_CPF }
  ],
  dataNascimento: [{ required: true, message: VALIDATION_MESSAGES.REQUIRED }],
  nomeMae: [{ required: true, message: VALIDATION_MESSAGES.REQUIRED }],
  municipioNaturalidade: [{ required: true, message: VALIDATION_MESSAGES.REQUIRED }],

  // Email residencial (obrigatório)
  emailResidencial: [
    { required: true, message: VALIDATION_MESSAGES.REQUIRED },
    { validator: (value) => validateEmail(value) ? null : VALIDATION_MESSAGES.INVALID_EMAIL }
  ],

  // Telefone (celular obrigatório)
  telefoneCelular: [
    { required: true, message: VALIDATION_MESSAGES.REQUIRED },
    { validator: (value) => validatePhone(value) ? null : VALIDATION_MESSAGES.INVALID_PHONE }
  ],

  // Endereço Residencial (obrigatório)
  cepResidencial: [
    { required: true, message: VALIDATION_MESSAGES.REQUIRED },
    {
      validator: (value, values) => {
        if (values.cepResidencialInvalido) return CEP_NAO_ENCONTRADO_MSG;
        if (values.cepResidencialPendente) return CEP_SELECIONE_ENDERECO_MSG;
        return validateCEP(value) ? null : VALIDATION_MESSAGES.INVALID_CEP;
      }
    }
  ],
  logradouroResidencial: [{ required: true, message: VALIDATION_MESSAGES.REQUIRED }],
  numeroResidencial: [{ required: true, message: VALIDATION_MESSAGES.REQUIRED }],
  bairroResidencial: [{ required: true, message: VALIDATION_MESSAGES.REQUIRED }],
  estadoResidencial: [{ required: true, message: VALIDATION_MESSAGES.REQUIRED }],
  municipioResidencial: [{ required: true, message: VALIDATION_MESSAGES.REQUIRED }],

  // Endereço Comercial - validação condicional (obrigatório só se correspondenciaComercial === 'S')
  cepComercial: [
    {
      required: (values) => values.correspondenciaComercial === 'S',
      message: 'CEP comercial é obrigatório quando seleciona correspondência'
    },
    {
      validator: (value, values) => {
        if (values.cepComercialInvalido) return CEP_NAO_ENCONTRADO_MSG;
        if (values.temEnderecoComercial && values.cepComercialPendente) return CEP_SELECIONE_ENDERECO_MSG;
        if (values.correspondenciaComercial === 'S' && !validateCEP(value)) {
          return VALIDATION_MESSAGES.INVALID_CEP;
        }
        return null;
      }
    }
  ],

  // Logradouro Comercial
  logradouroComercial: [
    {
      required: (values) => values.correspondenciaComercial === 'S',
      message: 'Logradouro comercial é obrigatório quando seleciona correspondência'
    }
  ],

  // Número Comercial
  numeroComercial: [
    {
      required: (values) => values.correspondenciaComercial === 'S',
      message: 'Número comercial é obrigatório quando seleciona correspondência'
    }
  ],

  // Bairro Comercial
  bairroComercial: [
    {
      required: (values) => values.correspondenciaComercial === 'S',
      message: 'Bairro comercial é obrigatório quando seleciona correspondência'
    }
  ],

  // Estado Comercial
  estadoComercial: [
    {
      required: (values) => values.correspondenciaComercial === 'S',
      message: 'Estado comercial é obrigatório quando seleciona correspondência'
    }
  ],

  // Município Comercial
  municipioComercial: [
    {
      required: (values) => values.correspondenciaComercial === 'S',
      message: 'Município comercial é obrigatório quando seleciona correspondência'
    }
  ],

  // País Comercial (opcional, mas se preenchido mantém)
  paisComercial: [], // Sem validação especial

  // Email Comercial - obrigatório só quando há endereço comercial (a API só
  // exige emailComercial de fato para PJ; para PF é opcional e só importa
  // quando correspondenciaComercial === 'S')
  emailComercial: [
    {
      required: (values) => values.correspondenciaComercial === 'S',
      message: 'E-mail comercial é obrigatório quando seleciona correspondência'
    },
    {
      validator: (value, values) => {
        if (values.correspondenciaComercial === 'S' && !validateEmail(value)) {
          return VALIDATION_MESSAGES.INVALID_EMAIL;
        }
        return null;
      }
    }
  ],

  // Telefone Comercial - opcional, mas válido se preenchido
  telefoneComercial: [
    {
      validator: (value) => !value || validatePhone(value) ? null : VALIDATION_MESSAGES.INVALID_PHONE
    }
  ],

  // Celular Comercial - opcional, mas válido se preenchido
  telefone2Comercial: [
    {
      validator: (value) => !value || validatePhone(value) ? null : VALIDATION_MESSAGES.INVALID_PHONE
    }
  ],


  // Senha
  password: [
    { required: true, message: VALIDATION_MESSAGES.REQUIRED },
    { validator: (value) => value.length >= 8 ? null : VALIDATION_MESSAGES.PASSWORD_MIN_LENGTH }
  ],
  confirmPassword: [
    { required: true, message: VALIDATION_MESSAGES.REQUIRED },
    { validator: (value, values) => value === values.password ? null : VALIDATION_MESSAGES.PASSWORD_MISMATCH }
  ]
};

const RegisterForm = ({ onToggleForm, onSubmit }) => {
  const [opcoes, setOpcoes] = useState({ municipios: [], estados: [], paises: [], loading: true });
  const [fetchError, setFetchError] = useState(null);
  const [alert, setAlert] = useState({ show: false, type: '', title: '', message: '' });
  const [documentoExistente, setDocumentoExistente] = useState(false);
  // Registros de CEPs com mais de um endereço, aguardando a pessoa escolher (por tipo)
  const [cepOpcoes, setCepOpcoes] = useState({ residencial: [], comercial: [] });
  const alertRef = useRef(null);

  const { buscarCEPDebounced } = useCEP();
  const {
    values, errors, touched, isSubmitting,
    handleChange, handleBlur, setFieldValue,
    handleSubmit: formHandleSubmit, setValues, setErrors
  } = useForm(INITIAL_REGISTER_FORM, validationRules);

  const scrollToAlert = useCallback(() => {
    setTimeout(() => {
      if (alertRef.current) {
        alertRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        alertRef.current.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
        setTimeout(() => {
          if (alertRef.current) alertRef.current.style.backgroundColor = '';
        }, 500);
      }
    }, 150);
  }, []);

  const showAlert = useCallback((type, title, message, autoClose = false) => {
    setAlert({ show: true, type, title, message, autoClose });
    scrollToAlert();
  }, [scrollToAlert]);

  useEffect(() => {
    const carregarOpcoes = async () => {
      try {
        setOpcoes(prev => ({
          ...prev,
          loading: true
        }));

        const [estados, paises] = await Promise.all([
          addressService.getStates(),
          addressService.getCountries()
        ]);

        setOpcoes({
          estados: estados.data,
          municipios: [],
          paises: paises.data,
          loading: false
        });

      } catch (error) {
        setFetchError('Erro ao carregar opções. Recarregue a página.');
      }
    };

    carregarOpcoes();
  }, []);

  const carregarMunicipios = useCallback(async (estadoId) => {
    if (!estadoId) {
      setOpcoes(prev => ({
        ...prev,
        municipios: []
      }));
      return;
    }

    try {
      const response = await addressService.getCities(estadoId);

      setOpcoes(prev => ({
        ...prev,
        municipios: response.data,
        loading: false
      }));

    } catch (err) {
      setOpcoes(prev => ({
        ...prev,
        municipios: [],
        loading: false
      }));
    }

  }, []);

  // Limpa o que veio de uma busca de CEP anterior: os campos de endereço são
  // preenchidos SÓ pela busca (ver AddressSection.js, disabled).
  const camposEnderecoVazios = (prefixo) => ({
    [`logradouro${prefixo}`]: '',
    [`bairro${prefixo}`]: '',
    [`estado${prefixo}`]: '',
    [`municipio${prefixo}`]: '',
    [`cep${prefixo}Id`]: ''
  });

  const handleCEPBlur = (e, tipo) => {
    handleBlur(e);
    const cep = e.target.value.replace(/\D/g, '');
    const prefixo = tipo.charAt(0).toUpperCase() + tipo.slice(1);
    const flagInvalido = `cep${prefixo}Invalido`;
    const flagPendente = `cep${prefixo}Pendente`;

    if (cep.length !== 8) {
      setCepOpcoes(prev => ({ ...prev, [tipo]: [] }));
      setValues(prev => ({ ...prev, [flagPendente]: false }));
      return;
    }

    buscarCEPDebounced(e.target.value, tipo, {
      onSuccess: (addressData, estadoId) => {
        setCepOpcoes(prev => ({ ...prev, [tipo]: [] }));
        setValues(prev => ({ ...prev, ...addressData, [flagInvalido]: false, [flagPendente]: false }));
        // Recarrega os municípios do estado retornado pelo CEP, senão o
        // select fica sem opções pra exibir o município preenchido
        if (estadoId) carregarMunicipios(estadoId);
      },
      onMultiplos: (registros) => {
        // CEP único de localidade (vários registros com o mesmo número): a pessoa
        // escolhe o dela em CepOpcoes; até lá `flagPendente` trava o avanço.
        // Mesmo CEP buscado de novo (ex: a pessoa só passou pelo campo): mantém a escolha.
        setCepOpcoes(prev => ({ ...prev, [tipo]: registros }));
        setValues(prev => {
          const manterEscolha = registros.some(r => String(r.id) === String(prev[`cep${prefixo}Id`]));
          return manterEscolha
            ? { ...prev, [flagInvalido]: false, [flagPendente]: false }
            : { ...prev, ...camposEnderecoVazios(prefixo), [flagInvalido]: false, [flagPendente]: true };
        });
        setErrors(prev => ({ ...prev, [`cep${prefixo}`]: null }));
      },
      onError: (errorData) => {
        // CEP não encontrado (ou erro técnico): limpa o endereço e trava o avanço via
        // `flagInvalido` (checado no validator de cepResidencial/cepComercial),
        // pedindo pra falar com um atendente da OAB/MS.
        setCepOpcoes(prev => ({ ...prev, [tipo]: [] }));
        setValues(prev => ({
          ...prev,
          ...camposEnderecoVazios(prefixo),
          [flagInvalido]: true,
          [flagPendente]: false
        }));
        setErrors(prev => ({ ...prev, ...errorData }));
        showAlert('error', 'CEP não encontrado', `Entre em contato com um atendente da OAB/MS pelos telefones (67) 3318-4782 ou (67) 3318-4778 para verificar esse CEP.`);
      }
    });
  };

  // Pessoa escolheu um dos registros de um CEP com vários endereços (CepOpcoes)
  const handleCepOpcaoSelect = (tipo, registro) => {
    const prefixo = tipo.charAt(0).toUpperCase() + tipo.slice(1);
    setValues(prev => ({
      ...prev,
      ...mapearEnderecoCadastro(registro, tipo),
      [`cep${prefixo}Pendente`]: false
    }));
    setErrors(prev => ({ ...prev, [`cep${prefixo}`]: null }));
    if (registro.estadoId) carregarMunicipios(registro.estadoId);
  };

  // Verifica se o documento já tem cadastro antes de liberar o resto do
  // formulário; só chama a API se o CPF/CNPJ já for válido localmente
  const handleDocumentoBlur = async (e) => {
    handleBlur(e);

    const documento = unformat(e.target.value);
    if (!validateCPF(documento)) {
      setDocumentoExistente(false);
      return;
    }

    try {
      const resultado = await cadastroService.verificarDocumento(documento);

      if (resultado.success && resultado.data?.existe) {
        setDocumentoExistente(true);
        setErrors(prev => ({ ...prev, cpfCnpj: 'Este CPF/CNPJ já possui cadastro' }));
        showAlert(
          'warning',
          'Documento já cadastrado',
          'Este CPF já está cadastrado. Faça login ou recupere sua senha.'
        );
      } else {
        setDocumentoExistente(false);
      }
    } catch (error) {
      // Falha na verificação (ex: rede) não deve travar o preenchimento do cadastro
      setDocumentoExistente(false);
    }
  };

  const handleCorrespondenciaChange = (tipo, valor, emailAtual) => {
    // Caso "Não": apenas atualiza
    if (valor === 'N') {
      setValues(prev => ({
        ...prev,
        [tipo === 'residencial' ? 'correspondenciaResidencial' : 'correspondenciaComercial']: 'N'
      }));
      return;
    }

    // Caso "Sim": valida email e aplica regras
    if (valor === 'S') {
      const emailExiste = emailAtual?.toString().trim() &&
        emailAtual !== 'undefined' &&
        emailAtual !== null;

      if (!emailExiste) {
        showAlert(
          'warning',
          `📧 E-mail ${tipo} obrigatório`,
          tipo === 'residencial'
            ? 'Para receber correspondência no endereço residencial, preencha o email primeiro!'
            : 'Você precisa preencher o email comercial para continuar!'
        );
        return;
      }

      // Se válido, marca este como "Sim" e o outro como "Não"
      setValues(prev => ({
        ...prev,
        [tipo === 'residencial' ? 'correspondenciaResidencial' : 'correspondenciaComercial']: 'S',
        [tipo === 'residencial' ? 'correspondenciaComercial' : 'correspondenciaResidencial']: 'N'
      }));

    }

  };

  const handleSubmitForm = async (data) => {
    if (documentoExistente) {
      showAlert('error', 'Documento já cadastrado', 'Corrija o CPF/CNPJ antes de continuar.');
      return;
    }

    try {
      // Monta o payload explicitamente conforme o contrato da API de cadastro
      // (pessoa física) — evita mandar campos internos do formulário (ex:
      // municipioResidencialNome, temEnderecoComercial) e garante os tipos
      // esperados (IDs de endereço/estado civil são number, não string).
      const dadosEnvio = {
        tipoPessoa: 'F',
        cpfCnpj: data.cpfCnpj,
        nome: data.nome,
        nomeSocial: data.nomeSocial || null,
        password: data.password,

        sexo: data.sexo,
        estadoCivil: Number(data.estadoCivil),
        dataNascimento: data.dataNascimento,
        nomeMae: data.nomeMae,
        nomePai: data.nomePai || null,
        municipioNaturalidade: data.municipioNaturalidade ? Number(data.municipioNaturalidade) : null,

        emailResidencial: data.emailResidencial,
        telefoneCelular: data.telefoneCelular,
        telefoneResidencial: data.telefoneResidencial || null,

        // A API espera o ID do registro de CEP (retornado pela API de
        // endereço em /api/cep/:cep), não o texto digitado pelo usuário
        cepResidencial: data.cepResidencialId || null,
        paisResidencial: Number(data.paisResidencial),
        estadoResidencial: Number(data.estadoResidencial),
        municipioResidencial: Number(data.municipioResidencial),
        bairroResidencial: data.bairroResidencial,
        numeroResidencial: data.numeroResidencial,
        logradouroResidencial: data.logradouroResidencial,
        complementoResidencial: data.complementoResidencial || null,
        correspondenciaResidencial: data.correspondenciaResidencial
      };

      // Bloco comercial é opcional para PF - só envia se o usuário preencheu
      if (data.temEnderecoComercial) {
        Object.assign(dadosEnvio, {
          cepComercial: data.cepComercialId || null,
          paisComercial: Number(data.paisComercial),
          estadoComercial: Number(data.estadoComercial),
          municipioComercial: Number(data.municipioComercial),
          bairroComercial: data.bairroComercial,
          numeroComercial: data.numeroComercial,
          logradouroComercial: data.logradouroComercial,
          complementoComercial: data.complementoComercial || null,
          emailComercial: data.emailComercial || null,
          telefoneComercial: data.telefoneComercial || null,
          telefone2Comercial: data.telefone2Comercial || null,
          correspondenciaComercial: data.correspondenciaComercial
        });
      }

      const result = await cadastroService.cadastroCompleto(dadosEnvio);

      if (result.success) {
        showAlert('success', 'Cadastro realizado!', 'Redirecionando...', true);
        setTimeout(() => {
          onSubmit?.(result);
          onToggleForm(); // Volta para login após cadastro
        }, 2000);
      } else {
        showAlert('error', 'Erro no cadastro', result.message || 'Tente novamente.');
      }
    } catch (error) {
      const mensagem = error.response?.data?.message;
      showAlert('error', 'Erro no cadastro', mensagem || 'Não foi possível conectar ao servidor.');
    }
  };

  return (
    <FormContainer>
      <BackButton onClick={onToggleForm}>
        <FaArrowLeft /> Voltar para o login
      </BackButton>

      <FormTitle>Cadastro de Novo Usuário</FormTitle>
      <FormSubtitle>Preencha todos os campos obrigatórios (*)</FormSubtitle>

      {/* Alertas */}
      {alert.show && (
        <Alert
          ref={alertRef}
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={() => setAlert({ show: false })}
          autoClose={alert.autoClose}
        />
      )}
      {fetchError && <Alert ref={alertRef} type="error" message={fetchError} onClose={() => setFetchError(null)} />}

      <form onSubmit={(e) => { e.preventDefault(); formHandleSubmit(handleSubmitForm); }}>
        {/* Seção 1: Informações Pessoais */}
        <PersonalInfoSection
          values={values} errors={errors} touched={touched}
          onChange={handleChange} onBlur={handleBlur}
          onCPFBlur={handleDocumentoBlur} opcoes={opcoes}
        />

        {/* Seção 2: Endereço Residencial */}
        <AddressSection
          type="residencial"
          values={values} errors={errors} touched={touched}
          onChange={handleChange} onBlur={handleBlur}
          onCEPBlur={handleCEPBlur} opcoes={opcoes}
          cepOpcoes={cepOpcoes.residencial}
          onCepOpcaoSelect={(registro) => handleCepOpcaoSelect('residencial', registro)}
          onCorrespondenciaChange={handleCorrespondenciaChange}
        />

        {/* Seção 3: Endereço Comercial (opcional) */}
        <AddressSection
          type="comercial"
          values={values} errors={errors} touched={touched}
          onChange={handleChange} onBlur={handleBlur}
          onCEPBlur={handleCEPBlur} opcoes={opcoes}
          cepOpcoes={cepOpcoes.comercial}
          onCepOpcaoSelect={(registro) => handleCepOpcaoSelect('comercial', registro)}
          showCommercial={values.temEnderecoComercial}
          onToggleCommercial={() => setFieldValue('temEnderecoComercial', !values.temEnderecoComercial)}
          onCorrespondenciaChange={handleCorrespondenciaChange}
        />

        {/* Seção 4: Senha */}
        <PasswordSection
          values={values} errors={errors} touched={touched}
          onChange={handleChange} onBlur={handleBlur}
        />

        {/* Botão Submit */}
        <Button
          type="submit"
          variant="primary"
          size="full"
          disabled={isSubmitting}
          leftIcon={<FaUserPlus />}
        >
          {isSubmitting ? 'Processando...' : 'Finalizar Cadastro'}
        </Button>
      </form>

      {/* Link para login */}
      <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
        <span style={{ color: '#666' }}>Já tem uma conta? </span>
        <Button variant="ghost" size="small" onClick={onToggleForm}>
          Faça login
        </Button>
      </div>
    </FormContainer>
  );
};

export default RegisterForm;